import React, { useState } from "react";
import {
  Mail,
  Phone,
  MapPin,
  ArrowUpRight,
  Check,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    website: "",
    message: "",
  });

  const [sent, setSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();

    const cleanForm = {
      name: form.name.trim(),
      email: form.email.trim(),
      website: form.website.trim(),
      message: form.message.trim(),
    };

    if (!cleanForm.name || !cleanForm.email) {
      toast.error("Please share your name and email.");
      return;
    }

    setIsSubmitting(true);
    setSent(false);

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cleanForm),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result.message || "Unable to submit your request."
        );
      }

      setSent(true);

      setForm({
        name: "",
        email: "",
        website: "",
        message: "",
      });

      toast.success(
        result.message ||
          "Thanks! We'll reach out within 24 hours."
      );

      setTimeout(() => {
        setSent(false);
      }, 3500);
    } catch (error) {
      console.error("Contact form error:", error);

      toast.error(
        error.message ||
          "Unable to send your request. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      <div
        className="absolute inset-0 brand-gradient-soft"
        aria-hidden
      />

      <div className="absolute -top-24 left-10 h-72 w-72 rounded-full brand-gradient blur-3xl opacity-25 blob-drift" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-3.5 py-1 text-xs font-medium text-brand-deep border border-[hsl(var(--brand-violet)/.15)]">
              Start Growing
            </div>

            <h2 className="mt-4 text-4xl sm:text-5xl font-bold text-[hsl(var(--brand-navy))] leading-[1.08]">
              Get started with development & marketing{" "}
              <span className="text-gradient-brand">today</span>
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              If you want a website that ranks higher, attracts
              quality traffic, and converts visitors into customers -
              request a free website audit and let&apos;s build your
              growth system.
            </p>

            <div className="mt-8 space-y-3">
              {[
                {
                  Icon: Mail,
                  key: "Email",
                  value: "support@rankifydev.com",
                },
                {
                  Icon: Phone,
                  key: "Phone",
                  value: "+91 93689 51593",
                },
                {
                  Icon: MapPin,
                  key: "Location",
                  value: "USA · India",
                },
              ].map(({ Icon, key, value }) => (
                <div
                  key={key}
                  className="flex items-center gap-3"
                >
                  <div className="h-10 w-10 rounded-xl brand-gradient grid place-items-center">
                    <Icon className="h-4 w-4 text-white" />
                  </div>

                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-slate-500">
                      {key}
                    </div>

                    <div className="text-sm font-semibold text-[hsl(var(--brand-navy))]">
                      {value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-3xl bg-white border border-black/5 card-shadow p-6 sm:p-8 space-y-4"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field
                  label="Your Name"
                  name="name"
                  value={form.name}
                  onChange={(value) =>
                    setForm({
                      ...form,
                      name: value,
                    })
                  }
                  testId="contact-name"
                  placeholder="Your full name"
                  required
                />

                <Field
                  label="Email Address"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={(value) =>
                    setForm({
                      ...form,
                      email: value,
                    })
                  }
                  testId="contact-email"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <Field
                label="Website URL (optional)"
                name="website"
                type="url"
                value={form.website}
                onChange={(value) =>
                  setForm({
                    ...form,
                    website: value,
                  })
                }
                testId="contact-website"
                placeholder="https://example.com"
              />

              <div>
                <label
                  htmlFor="contact-message"
                  className="text-xs font-semibold text-slate-500 uppercase tracking-wider"
                >
                  Project Details
                </label>

                <textarea
                  id="contact-message"
                  name="message"
                  value={form.message}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      message: e.target.value,
                    })
                  }
                  data-testid="contact-message"
                  rows={4}
                  placeholder="Tell us about your project, goals or challenges..."
                  className="mt-1.5 w-full rounded-xl border border-black/10 bg-lilac/40 px-4 py-3 text-sm text-[hsl(var(--brand-navy))] focus:outline-none focus:ring-2 focus:ring-brand/30 resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                data-testid="contact-submit"
                className={`group w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-all ${
                  sent
                    ? "bg-emerald-600 text-white"
                    : "bg-[hsl(var(--brand-navy))] hover:bg-brand text-white"
                } ${
                  isSubmitting
                    ? "opacity-70 cursor-not-allowed"
                    : ""
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : sent ? (
                  <>
                    <Check className="h-4 w-4" />
                    Sent successfully
                  </>
                ) : (
                  <>
                    Request Free Audit
                    <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  testId,
  placeholder = "",
  required = false,
}) => (
  <div>
    <label
      htmlFor={testId}
      className="text-xs font-semibold text-slate-500 uppercase tracking-wider"
    >
      {label}
    </label>

    <input
      id={testId}
      name={name}
      type={type}
      value={value}
      required={required}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="mt-1.5 w-full rounded-xl border border-black/10 bg-lilac/40 px-4 py-3 text-sm text-[hsl(var(--brand-navy))] focus:outline-none focus:ring-2 focus:ring-brand/30"
    />
  </div>
);

export default Contact;